/**
 * Robbie's per-step content for the docked panel — the greeting his resting
 * state opens with, the pinned NEXT BEST STEP card, the quick-prompt chips
 * shown until the backend serves dynamic ones, and the journey-rail tip.
 * Voice rules: first person, consequence, next action.
 */
export interface RobbieStepContent {
  /**
   * The single line Robbie opens a cold conversation with. Deliberately one
   * line: it sits under the mark with the step's suggestion chips right below
   * it, so it says where we are and what to hand him — the chips carry the
   * specifics.
   */
  restingGreeting: string;
  tip: string;
  nextBestStep?: { label: string; actionId?: string };
  prompts: string[];
}

const CONTENT: Record<number, RobbieStepContent> = {
  1: {
    restingGreeting: "Ready when you are — hand me your BOM and I'll pull out every component and spec.",
    tip: 'A detailed BOM helps me find the best suppliers faster.',
    nextBestStep: { label: 'Select files to begin', actionId: 'upload_bom' },
    prompts: ['See sample BOM', 'What type of file should I upload?'],
  },
  2: {
    restingGreeting: "Tell me what matters in a supplier and I'll shape the whole search around it.",
    tip: 'The tighter your criteria, the sharper my supplier shortlist.',
    nextBestStep: { label: 'Fill sourcing strategy & logistics', actionId: 'scroll_to_bottom' },
    prompts: ['Suggest standard criteria', 'Compare compliance needs', 'What are common constraints?'],
  },
  3: {
    restingGreeting: 'These questions steer where I search — ask me about any of them and I’ll work through it with you.',
    tip: 'Your answers steer where I search — precise context finds better suppliers.',
    prompts: [
      'Why are these questions asked?',
      'Explain recommendation',
      'Compare shortlisted suppliers',
      'Draft outreach email',
    ],
  },
  4: {
    restingGreeting: "Your threads are live — ask me about any supplier reply and I'll dig into it.",
    tip: "Replies land in these threads on their own — I'll flag the ones that need you.",
    prompts: ['Summarize supplier responses', 'Compare quotes so far', "What's the status of my RFQ?"],
  },
  5: {
    restingGreeting: "The quotes are in — ask me where the real differences sit.",
    tip: 'I weigh quotes on price, lead time, and risk — never price alone.',
    prompts: ['Explain quote differences', 'Suggest negotiation points', 'Flag risky terms', 'Prepare approval summary'],
  },
};

/** Discovery (step 3, sub-step 3): the context step's questions are behind the buyer by now. */
const DISCOVERY: RobbieStepContent = {
  restingGreeting: "Ask me about the suppliers I've found so far. I'll keep researching the rest.",
  tip: 'Shortlist the suppliers you want me to contact — I pre-pick my top choice for each part.',
  prompts: ['Why these suppliers?', 'Compare shortlisted suppliers', 'Draft outreach email'],
};

const FALLBACK: RobbieStepContent = {
  restingGreeting: "I'm here whenever you need me — ask and I'll pick it up from your RFQ.",
  tip: 'Every step you finish unlocks the next one — I keep the journey honest.',
  prompts: ['How does the process work?'],
};

export const robbieContentForStep = (step: number, subStep?: number): RobbieStepContent =>
  step === 3 && subStep === 3 ? DISCOVERY : CONTENT[step] ?? FALLBACK;

/**
 * The pinned NEXT BEST STEP card, derived from real position and progress —
 * never a static per-step label. A card renders only when it points at an
 * action beyond the page the buyer is already on: steps 1–2 open/scroll to
 * their own work, step 3 offers the exit once a shortlist is saved, and
 * steps 4–5 show nothing here because those surfaces carry their own action
 * systems (outreach action cards, the award builder).
 */
export const nextBestStepFor = (
  pos: { step: number; subStep: number },
  readiness: { shortlistDone: boolean },
): { label: string; actionId?: string } | undefined => {
  if (pos.step <= 2) return CONTENT[pos.step]?.nextBestStep;
  if (pos.step === 3 && pos.subStep === 3 && readiness.shortlistDone) {
    return { label: 'Continue to Supplier Outreach', actionId: 'continue_to_outreach' };
  }
  return undefined;
};
